import { ApplicationConfig, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, Routes, withComponentInputBinding } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Layouts } from './app';

// Components
import { HeaderComponent } from './header/header.component';
import { MainComponent } from './main/main.component';
import { NotAuthorizedComponent } from './shared/components/not-authorized/not-authorized.component';
import { SignInCallbackComponent } from './signincallback/signincallback.component';
import { AccountConfirmationComponent } from './account-confirmation/account-confirmation.component';
import { SigninPageComponent } from './signin-page/signin-page.component';
import { InvitationComponent } from './invitation/invitation.component';

// Guards
import { TreeGuard } from './guards/tree.guard';
import { TreeSettingsGuard } from './guards/treesettings.guard';
import { AccountActivationGuard } from './guards/accountactivation.guard';
import { UserSettingsGuard } from './guards/usersettings.guard';
import { SiteAdminGuard } from './guards/siteadmin.guard';

// Services
import { AuditService } from './shared/services/audit.service';
import { AuthService } from './shared/services/auth.service';
import { EventService } from './shared/services/event.service';
import { FamilyService } from './shared/services/family.service';
import { FileService } from './shared/services/file.service';
import { GedcomService } from './shared/services/gedcom.service';
import { MainService } from './shared/services/main.service';
import { PersonService } from './shared/services/person.service';
import { PdfExportService } from './shared/services/pdf-export.service';
import { PlaceService } from './shared/services/place.service';
import { SourceService } from './shared/services/source.service';
import { SubscriptionService } from './shared/services/subscription.service';
import { TagService } from './shared/services/tag.service';
import { TreeService } from './shared/services/tree.service';
import { MessageService } from './shared/services/message.service';
import { UserService } from './shared/services/user.service';
import { CookieService } from 'ngx-cookie-service';
import { Globals } from './shared/globals';

// Interceptors
import { AuthInterceptor } from './shared/interceptors/auth.interceptor';

// Resolvers
import { TreeResolve } from './shared/resolvers/tree.resolve';

// Tlumaczenia
import { I18nService } from './shared/i18n/i18n.service';
import { PageTitleService } from './shared/services/page-title.service';
import { ConsentService } from './shared/services/consent.service';

const appRoutes: Routes = [
  {
    path: '',
    component: HeaderComponent,
    outlet: 'header',
    pathMatch: 'full'
  },
  {
    path: '',
    component: MainComponent,
    data: { layout: Layouts.simple }
  },   {
    // Naglowek jako trasa potomna z pusta sciezka - inaczej pasek u gory
    // nie ma czego pokazac i znika
    path: 'not-authorized', data: { title: 'title.notAuthorized', layout: Layouts.simple },
    
    children: [
      { path: '', component: NotAuthorizedComponent },
      { path: '', component: HeaderComponent, outlet: 'header' }
    ]
  },
  {
    // Naglowek jako trasa potomna z pusta sciezka - inaczej pasek u gory
    // nie ma czego pokazac i znika
    path: 'signin', data: { title: 'title.signin', layout: Layouts.simple },
    
    children: [
      { path: '', component: SigninPageComponent },
      { path: '', component: HeaderComponent, outlet: 'header' }
    ]
  },
  {
    path: 'signin/callback',
    component: SignInCallbackComponent
  },
  {
    // Aktywacja konta z linku w mailu - prosty layout z naglowkiem, jak inne
    // strony z linkow w poczcie; zalogowanego odsyla straznik na strone glowna
    path: 'account/confirmation/:confirmationCode',
    data: { title: 'title.accountConfirmation', layout: Layouts.simple },
    canActivate: [AccountActivationGuard],
    children: [
      { path: '', component: AccountConfirmationComponent },
      { path: '', component: HeaderComponent, outlet: 'header' }
    ]
  },
  {
    // Historia zmian - wchodzi sie tu z numeru wersji w stopce, wiec musi
    // dzialac takze bez zalogowania i bez wybranego drzewa
    path: 'changelog', data: { title: 'title.changelog', layout: Layouts.simple },
    
    children: [
      { path: '', loadComponent: () => import('./changelog/changelog.component')
          .then(m => m.ChangelogComponent) },
      { path: '', component: HeaderComponent, outlet: 'header' }
    ]
  },
  {
    // Potwierdzenie adresu nadawcy pytania o profil - link z maila. Musi
    // dzialac bez logowania i bez wybranego drzewa
    path: 'profile-message/confirm/:key',
    data: { title: 'title.profileContact', layout: Layouts.simple },

    children: [
      { path: '', loadComponent: () => import('./profile-message-confirm/profile-message-confirm.component')
          .then(m => m.ProfileMessageConfirmComponent) },
      { path: '', component: HeaderComponent, outlet: 'header' }
    ]
  },
  {
    // Wyszukiwanie osob po wielu drzewach - strona publiczna; zakres drzew
    // i osoby zyjace filtruje backend, nie ta trasa
    path: 'search', data: { title: 'title.search', layout: Layouts.simple },

    children: [
      { path: '', loadComponent: () => import('./search/search.component')
          .then(m => m.SearchComponent) },
      { path: '', component: HeaderComponent, outlet: 'header' }
    ]
  },
  {
    // Cennik planow abonamentowych - dostepny takze bez logowania
    // /pricing pokazuje CALA strone glowna przewinieta do cennika, a nie sam
    // cennik na pustym tle - scrollTo czyta MainComponent.ngOnInit()
    path: 'pricing', data: { title: 'title.pricing', layout: Layouts.simple, scrollTo: 'pricing' },

    children: [
      { path: '', component: MainComponent },
      { path: '', component: HeaderComponent, outlet: 'header' }
    ]
  },
  {
    // Naglowek jako trasa potomna z pusta sciezka - tylko takie wpisy
    // trafiaja na nazwany outlet bez segmentu (header:...) w adresie
    path: 'privacy', data: { title: 'title.privacy', layout: Layouts.simple },
    
    children: [
      { path: '', loadComponent: () => import('./legal/privacy/privacy.component')
          .then(m => m.PrivacyComponent) },
      { path: '', component: HeaderComponent, outlet: 'header' }
    ]
  },
  {
    // Naglowek jako trasa potomna z pusta sciezka - tylko takie wpisy
    // trafiaja na nazwany outlet bez segmentu (header:...) w adresie
    path: 'terms', data: { title: 'title.terms', layout: Layouts.simple },
    
    children: [
      { path: '', loadComponent: () => import('./legal/terms/terms.component')
          .then(m => m.TermsComponent) },
      { path: '', component: HeaderComponent, outlet: 'header' }
    ]
  },
  {
    // Naglowek jako trasa potomna z pusta sciezka - tylko takie wpisy
    // trafiaja na nazwany outlet bez segmentu (header:...) w adresie
    path: 'data-deletion', data: { title: 'title.dataDeletion', layout: Layouts.simple },
    
    children: [
      { path: '', loadComponent: () => import('./legal/data-deletion/data-deletion.component')
          .then(m => m.DataDeletionComponent) },
      { path: '', component: HeaderComponent, outlet: 'header' }
    ]
  },
  {
    // Naglowek jako trasa potomna z pusta sciezka - tylko takie wpisy
    // trafiaja na nazwany outlet bez segmentu (header:...) w adresie
    path: 'refunds', data: { title: 'title.refunds', layout: Layouts.simple },

    children: [
      { path: '', loadComponent: () => import('./legal/refunds/refunds.component')
          .then(m => m.RefundsComponent) },
      { path: '', component: HeaderComponent, outlet: 'header' }
    ]
  },
  {
    // Naglowek jako trasa potomna z pusta sciezka - inaczej pasek u gory
    // nie ma czego pokazac i znika
    path: 'invitation/:token', data: { title: 'title.invitation', layout: Layouts.simple },
    
    children: [
      { path: '', component: InvitationComponent },
      { path: '', component: HeaderComponent, outlet: 'header' }
    ]
  },
  {
    path: 'user-settings',
    loadChildren: () => import('./user-settings/user-settings.module').then(m => m.UserSettingsModule),
    canMatch: [UserSettingsGuard],
    data: { layout: Layouts.main }
  },
  {
    path: 'user-settings',
    loadChildren: () => import('./user-settings/user-settings.module').then(m => m.UserSettingsModule), outlet: 'navbar'
  },
  {
    path: 'user-settings',
    loadChildren: () => import('./user-settings/user-settings.module').then(m => m.UserSettingsModule), outlet: 'toolbar'
  },
  {
    path: 'user-settings',
    loadChildren: () => import('./user-settings/user-settings.module').then(m => m.UserSettingsModule), outlet: 'header'
  },
  // Panel administratora serwisu - bramka na kazdym outlecie (jak nizej)
  {
    path: 'admin',
    loadChildren: () => import('./admin/admin.module').then(m => m.AdminModule),
    canMatch: [SiteAdminGuard],
    data: { layout: Layouts.main }
  },
  {
    path: 'admin',
    loadChildren: () => import('./admin/admin.module').then(m => m.AdminModule),
    canMatch: [SiteAdminGuard], outlet: 'navbar'
  },
  {
    path: 'admin',
    loadChildren: () => import('./admin/admin.module').then(m => m.AdminModule),
    canMatch: [SiteAdminGuard], outlet: 'toolbar'
  },
  {
    path: 'admin',
    loadChildren: () => import('./admin/admin.module').then(m => m.AdminModule),
    canMatch: [SiteAdminGuard], outlet: 'header'
  },
  // Bramka na KAZDYM outlecie - inaczej zablokowany zostaje tylko widok
  // glowny, a naglowek, menu i pasek narzedzi ustawien drzewa i tak sie
  // renderuja dla osoby bez uprawnien
  {
    path: 'tree/:tree/settings',
    loadChildren: () => import('./tree-settings/tree-settings.module').then(m => m.TreeSettingsModule),
    canMatch: [TreeSettingsGuard],
    data: { layout: Layouts.main }
  },
  {
    path: 'tree/:tree/settings',
    loadChildren: () => import('./tree-settings/tree-settings.module').then(m => m.TreeSettingsModule),
    canMatch: [TreeSettingsGuard], outlet: 'navbar'
  },
  {
    path: 'tree/:tree/settings',
    loadChildren: () => import('./tree-settings/tree-settings.module').then(m => m.TreeSettingsModule),
    canMatch: [TreeSettingsGuard], outlet: 'toolbar'
  },
  {
    path: 'tree/:tree/settings',
    loadChildren: () => import('./tree-settings/tree-settings.module').then(m => m.TreeSettingsModule),
    canMatch: [TreeSettingsGuard], outlet: 'header'
  },
  {
    path: 'tree',
    canActivate: [TreeGuard],
    loadChildren: () => import('./tree-main/tree-main.module').then(m => m.TreeMainModule),
    data: { layout: Layouts.main },
  },
  {
    path: 'tree',
    loadChildren: () => import('./tree-main/tree-main.module').then(m => m.TreeMainModule),
    outlet: 'navbar'
  },
  {
    path: 'tree',
    loadChildren: () => import('./tree-main/tree-main.module').then(m => m.TreeMainModule),
    outlet: 'header'
  },
  {
    path: 'tree',
    loadChildren: () => import('./tree-main/tree-main.module').then(m => m.TreeMainModule),
    outlet: 'toolbar',
  }
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes, withComponentInputBinding()),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    // Services
    AuditService,
    AuthService,
    // jedna instancja na aplikacje - potok `t` i komponenty musza widziec
    // ten sam wybrany jezyk
    I18nService,
    PageTitleService,
    EventService,
    FamilyService,
    FileService,
    GedcomService,
    MainService,
    PdfExportService,
    PersonService,
    PlaceService,
    SourceService,
    SubscriptionService,
    TagService,
    TreeService,
    MessageService,
    UserService,
    CookieService,
    ConsentService,
    Globals,
    // Guards
    TreeGuard,
    TreeSettingsGuard,
    AccountActivationGuard,
    UserSettingsGuard,
    SiteAdminGuard,
    // Resolvers
    TreeResolve,
    // Other providers
    { provide: LOCALE_ID, useValue: 'pl' },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }
  ]
};
